import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useSpeechSynthesisContext } from "../../../../hooks/SpeechSynthesisContext";
import { getFirstNames } from "../utils/utils";
import ServingBox from "./ServingBox";
import AnnouncementStatusIndicator from "./AnnouncementStatusIndicator";

function NowServing({ nowServing, speechConfig }) {
  const leftWindows = [
    "Counter 1",
    "Counter 2",
    "Counter 3",
    "Counter 4",
    "Counter 5",
    "Counter 6",
  ];
  const rightCategories = [
    "Counter 7",
    "Resignation",
    "Final Pay",
    "Orientation",
    "COE",
    "Others",
  ];
  const allLabels = useMemo(() => [...leftWindows, ...rightCategories], []);

  const { speak } = useSpeechSynthesisContext();
  const prevIdsRef = useRef({});
  const spokenRef = useRef({});
  const dingAudioRef = useRef(null);
  const announcingRef = useRef(false);

  const [servingQueue, setServingQueue] = useState([]);
  const [currentServing, setCurrentServing] = useState(null);
  const [announcingLabel, setAnnouncingLabel] = useState(null);

  const getLastServed = useCallback(
    (filterKey) => {
      const filtered = nowServing
        ?.filter(
          (item) => item?.status === "serving" && item?.window === filterKey,
        )
        ?.sort((a, b) => new Date(a.updated) - new Date(b.updated));
      return filtered?.slice(-1)[0] || null;
    },
    [nowServing],
  );

  useEffect(() => {
    try {
      const audio = new Audio("/ding.mp3");
      audio.preload = "auto";
      dingAudioRef.current = audio;
    } catch (e) {
      console.warn("Failed to initialize ding audio:", e);
    }
  }, []);

  useEffect(() => {
    const changedLabels = [];
    allLabels.forEach((label) => {
      const lastServed = getLastServed(label);
      const prevId = prevIdsRef.current[label];
      if (lastServed && lastServed.id && lastServed.id !== prevId) {
        changedLabels.push(label);
        prevIdsRef.current[label] = lastServed.id;
      }
    });
    if (changedLabels.length) {
      setServingQueue((prev) => [...prev, ...changedLabels]);
    }
  }, [nowServing, getLastServed, allLabels]);

  const handleSpeechComplete = useCallback((label) => {
    setServingQueue((queue) => {
      if (label) {
        return queue.filter((item) => item !== label);
      }
      return queue.slice(1);
    });
  }, []);

  useEffect(() => {
    if (servingQueue.length > 0) {
      setCurrentServing(servingQueue[0]);
    } else {
      setCurrentServing(null);
    }
  }, [servingQueue]);

  useEffect(() => {
    if (!currentServing || announcingRef.current) return;

    const lastServed = getLastServed(currentServing);
    if (!lastServed?.id) {
      handleSpeechComplete(currentServing);
      return;
    }

    const speechKey = `${currentServing}-${lastServed.id}`;
    if (spokenRef.current[speechKey]) {
      return;
    }

    const announce = async () => {
      announcingRef.current = true;
      spokenRef.current[speechKey] = true;
      setAnnouncingLabel(currentServing);

      try {
        const dingAudio = dingAudioRef.current;
        if (dingAudio) {
          dingAudio.pause();
          dingAudio.currentTime = 0;
          await dingAudio.play().catch((e) => {
            console.warn("Ding audio play failed:", e);
          });
        }
      } catch (e) {
        console.warn("Audio play failed:", e);
      }

      const firstNames = getFirstNames(lastServed.name);
      const options = {
        voice_name: speechConfig?.voice_name,
        speech_rate: speechConfig?.speech_rate,
        speech_pitch: speechConfig?.speech_pitch,
        speech_volume: speechConfig?.speech_volume,
        repetition_count: speechConfig?.repetition_count ?? 1,
        repeatDelay: 500,
      };

      try {
        await speak(`Now serving ${firstNames} at ${currentServing}`, options);
      } catch (error) {
        console.error("Speech error:", error);
      } finally {
        announcingRef.current = false;
        setAnnouncingLabel(null);
        handleSpeechComplete(currentServing);
      }
    };

    announce();
  }, [
    currentServing,
    getLastServed,
    speechConfig,
    speak,
    handleSpeechComplete,
  ]);

  return (
    <>
      <div className="d-flex align-items-center gap-2 mb-3">
        <b style={{ color: "#d9822b", fontSize: "1.5rem" }}>Now Serving</b>
        <AnnouncementStatusIndicator />
      </div>
      <div className="row">
        <div className="col-md-6">
          {leftWindows.map((label) => (
            <ServingBox
              key={label}
              label={label}
              lastServed={getLastServed(label)}
              isAnnouncing={announcingLabel === label}
            />
          ))}
        </div>
        <div className="col-md-6">
          {rightCategories.map((label) => (
            <ServingBox
              key={label}
              label={label}
              lastServed={getLastServed(label)}
              isAnnouncing={announcingLabel === label}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </>
  );
}

export default NowServing;
